import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminFilmsService } from '../services/admin.films.service'
import type { FilmDocument } from '@/types'
import type { FilmFormValues } from '../types'

interface UseAdminFilmsResult {
  films: Array<FilmDocument & { id: string }>
  loading: boolean
  error: string | null
  createFilm: (data: FilmFormValues) => Promise<string>
  updateFilm: (id: string, data: Partial<FilmFormValues>) => Promise<void>
  deleteFilm: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminFilms(seasonFilter?: number): UseAdminFilmsResult {
  const [films, setFilms] = useState<Array<FilmDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    const fetchPromise =
      seasonFilter !== undefined
        ? adminFilmsService.getFilmsBySeason(seasonFilter)
        : adminFilmsService.getAllFilms()

    fetchPromise
      .then(setFilms)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load films'))
      )
      .finally(() => setLoading(false))
  }, [tick, seasonFilter])

  const createFilm = useCallback(async (data: FilmFormValues): Promise<string> => {
    const id = await adminFilmsService.createFilm(data)
    refresh()
    return id
  }, [refresh])

  const updateFilm = useCallback(
    async (id: string, data: Partial<FilmFormValues>): Promise<void> => {
      await adminFilmsService.updateFilm(id, data)
      refresh()
    },
    [refresh]
  )

  const deleteFilm = useCallback(async (id: string): Promise<void> => {
    await adminFilmsService.deleteFilm(id)
    refresh()
  }, [refresh])

  return { films, loading, error, createFilm, updateFilm, deleteFilm, refresh }
}
